
// Modify the validation in useOnboardingForm.tsx to match the updated activity frequency values

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useUI } from "@/context/UIContext";
import { supabase, getEdgeFunctionUrl } from "@/integrations/supabase/client";

export interface OnboardingFormData {
  // Personal Info
  name: string;
  email: string;
  password?: string;
  confirmPassword?: string;
  age: string;
  gender: string;
  heightFeet: string;
  heightInches: string;
  weight: string;
  
  // Nutrition
  dietType: string;
  mealsPerDay: string;
  waterIntake: string;
  dietaryRestrictions: string[];
  allergies: string;
  
  // Fitness
  fitnessLevel: string;
  activityFrequency: string;
  preferredExercises: string[];
  fitnessGoals: string;
  
  // Sleep
  averageSleep: string;
  sleepQuality: string;
  sleepIssues: string[];
  
  // Supplements
  currentSupplements: string;
  supplementGoals: string;
  
  // Mindfulness
  stressLevel: string;
  mindfulnessPractices: string[];
  mentalHealthConcerns: string;
  
  // Wellness Goals
  primaryGoals: string[];
  secondaryGoals: string[];
  timeframe: string;
  additionalNotes: string;
}

export function useOnboardingForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { onboardingStep, setOnboardingStep, setIsOnboardingComplete } = useUI();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<OnboardingFormData>({
    // Personal Info
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    age: "",
    gender: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    
    // Nutrition
    dietType: "",
    mealsPerDay: "",
    waterIntake: "",
    dietaryRestrictions: [] as string[],
    allergies: "",
    
    // Fitness
    fitnessLevel: "",
    activityFrequency: "",
    preferredExercises: [] as string[],
    fitnessGoals: "",
    
    // Sleep
    averageSleep: "",
    sleepQuality: "",
    sleepIssues: [] as string[],
    
    // Supplements
    currentSupplements: "",
    supplementGoals: "",
    
    // Mindfulness
    stressLevel: "",
    mindfulnessPractices: [] as string[],
    mentalHealthConcerns: "",
    
    // Wellness Goals
    primaryGoals: [] as string[],
    secondaryGoals: [] as string[],
    timeframe: "",
    additionalNotes: "",
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (list: string, value: string) => {
    const currentList = formData[list as keyof typeof formData] as string[];
    
    if (currentList.includes(value)) {
      setFormData({
        ...formData,
        [list]: currentList.filter((item) => item !== value),
      });
    } else {
      setFormData({
        ...formData,
        [list]: [...currentList, value],
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRadioChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const nextStep = () => {
    if (onboardingStep < 6) {
      setOnboardingStep(onboardingStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (onboardingStep > 0) {
      setOnboardingStep(onboardingStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const validateRequiredFields = () => {
    // Validate required fields based on current step
    switch (onboardingStep) {
      case 0: // Personal Info
        if (!formData.name || !formData.email || !formData.age || !formData.gender) {
          return "Please fill in all personal information fields.";
        }
        if (!formData.heightFeet || !formData.heightInches || !formData.weight) {
          return "Please select your height and weight.";
        }
        if (!formData.password) {
          return "Please create a password.";
        }
        if (formData.password.length < 6) {
          return "Password must be at least 6 characters long.";
        }
        if (formData.password !== formData.confirmPassword) {
          return "Passwords do not match.";
        }
        break;
      case 1: // Nutrition
        if (!formData.dietType || !formData.mealsPerDay || !formData.waterIntake) {
          return "Please fill in all required nutrition fields.";
        }
        break;
      case 2: // Fitness
        if (!formData.fitnessLevel || !formData.activityFrequency) {
          return "Please select your fitness level and activity frequency.";
        }
        // Validate that activity frequency is a valid enum value
        const validActivityLevels = ["low", "moderate", "high", "very-high"];
        if (!validActivityLevels.includes(formData.activityFrequency)) {
          return "Please select a valid activity frequency.";
        }
        break;
      case 3: // Sleep
        if (!formData.averageSleep || !formData.sleepQuality) {
          return "Please indicate your sleep duration and quality.";
        }
        break;
      case 4: // Supplements
        // No required fields
        break;
      case 5: // Mindfulness
        if (!formData.stressLevel) {
          return "Please select your stress level.";
        }
        break;
      case 6: // Goals
        if (!formData.timeframe) {
          return "Please select your goal timeframe.";
        }
        if (formData.primaryGoals.length === 0) {
          return "Please select at least one primary goal.";
        }
        break;
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Validate required fields
    const validationError = validateRequiredFields();
    if (validationError) {
      toast({
        title: "Required Fields",
        description: validationError,
        variant: "destructive"
      });
      return;
    }
    
    if (onboardingStep < 6) {
      nextStep();
      return;
    }
    
    setLoading(true);
    
    try {
      // Store form data in localStorage for later use
      localStorage.setItem('onboardingData', JSON.stringify(formData));
      
      // Get plan info from localStorage
      const selectedPlan = localStorage.getItem('selectedPlan') || 'free';
      const selectedPriceId = localStorage.getItem('selectedPriceId') || 'free_tier';

      console.log("Processing registration for plan:", selectedPlan);

      // Check if this is for premium plan that needs checkout first
      if (selectedPlan === 'premium' && !sessionStorage.getItem('checkoutComplete')) {
        // Premium plan without checkout - redirect to checkout first
        navigate('/checkout/redirect');
        return;
      }

      // For free plan or premium after checkout completion - proceed with registration
      
      // Register the user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password || '',
        options: {
          data: {
            // Store user metadata
            name: formData.name,
            plan: selectedPlan,
          },
          emailRedirectTo: `${window.location.origin}/auth?mode=email_confirmed`
        }
      });

      if (signUpError) {
        throw signUpError;
      }

      console.log("User registered:", signUpData);
      
      // Save onboarding data to user_profiles
      if (signUpData.user) {
        // Insert onboarding data into user_profiles
        try {
          // Prepare allergies as an array for consistency
          const allergiesArray = formData.allergies ? [formData.allergies] : [];
          
          // Create supplements array from the single string value
          const supplementsArray = formData.currentSupplements ? [formData.currentSupplements] : [];
          
          // Format height as feet and inches
          const heightString = `${formData.heightFeet}'${formData.heightInches}"`;
          
          // Map activity frequency to a valid enum value
          let activityLevel = "low"; // Default value
          
          if (formData.activityFrequency === "0") activityLevel = "low";
          else if (formData.activityFrequency === "1-2") activityLevel = "moderate";
          else if (formData.activityFrequency === "3-4") activityLevel = "high";
          else if (formData.activityFrequency === "5+") activityLevel = "very-high";
          else activityLevel = formData.activityFrequency || "low";
          
          console.log("Creating user profile with data:", {
            user_id: signUpData.user.id,
            name: formData.name,
            email: formData.email,
            height: heightString,
            activity_level: activityLevel,
            // Other important fields for debugging
            primaryGoals: formData.primaryGoals,
            dietaryRestrictions: formData.dietaryRestrictions,
            preferredExercises: formData.preferredExercises,
            sleep_issues: formData.sleepIssues,
            mindfulness_practices: formData.mindfulnessPractices
          });
          
          // First try inserting user profile
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: signUpData.user.id,
              name: formData.name,
              email: formData.email,
              age: formData.age,
              gender: formData.gender,
              height: heightString,
              weight: formData.weight,
              dietary_restrictions: formData.dietaryRestrictions,
              food_preferences: [formData.dietType],
              water_intake: formData.waterIntake,
              allergies: allergiesArray,
              fitness_level: formData.fitnessLevel,
              activity_level: activityLevel,
              preferred_exercises: formData.preferredExercises,
              goals: formData.primaryGoals,
              sleep_hours: formData.averageSleep,
              sleep_quality: formData.sleepQuality,
              sleep_issues: formData.sleepIssues,
              medical_conditions: formData.sleepIssues,
              supplements: supplementsArray,
              supplement_goals: formData.supplementGoals,
              stress_level: formData.stressLevel,
              mindfulness_practices: formData.mindfulnessPractices,
              wellness_goals: formData.primaryGoals,
              health_concerns: formData.mentalHealthConcerns,
              timeframe: formData.timeframe,
              meals_per_day: formData.mealsPerDay
            });

          if (profileError) {
            console.error("Profile insertion error:", profileError);
            
            // If insert fails, try update
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({
                name: formData.name,
                email: formData.email,
                age: formData.age,
                gender: formData.gender,
                height: heightString,
                weight: formData.weight,
                dietary_restrictions: formData.dietaryRestrictions,
                food_preferences: [formData.dietType],
                water_intake: formData.waterIntake,
                allergies: allergiesArray,
                fitness_level: formData.fitnessLevel,
                activity_level: activityLevel,
                preferred_exercises: formData.preferredExercises,
                goals: formData.primaryGoals,
                sleep_hours: formData.averageSleep,
                sleep_quality: formData.sleepQuality,
                sleep_issues: formData.sleepIssues,
                medical_conditions: formData.sleepIssues,
                supplements: supplementsArray,
                supplement_goals: formData.supplementGoals,
                stress_level: formData.stressLevel,
                mindfulness_practices: formData.mindfulnessPractices,
                wellness_goals: formData.primaryGoals,
                health_concerns: formData.mentalHealthConcerns,
                timeframe: formData.timeframe,
                meals_per_day: formData.mealsPerDay
              })
              .eq('user_id', signUpData.user.id);
              
            if (updateError) {
              console.error("Profile update error:", updateError);
              throw updateError;
            } else {
              console.log("Profile updated successfully");
            }
          } else {
            console.log("User profile data saved successfully");
          }
          
          // Ensure the user has vita credits
          // First check if credits already exist
          const { data: existingCredits, error: checkCreditsError } = await supabase
            .from('vita_credits')
            .select('*')
            .eq('user_id', signUpData.user.id)
            .maybeSingle();
            
          if (checkCreditsError) {
            console.error("Error checking for existing credits:", checkCreditsError);
          }
          
          // Only insert if no existing credits
          if (!existingCredits) {
            const { data: creditData, error: creditError } = await supabase
              .from('vita_credits')
              .insert({
                user_id: signUpData.user.id,
                credits_limit: 100,
                credits_used: 0
              })
              .select();
              
            if (creditError) {
              console.error("Error ensuring vita credits:", creditError);
              
              // Try direct insert without returning data
              const { error: simpleInsertError } = await supabase
                .from('vita_credits')
                .insert({
                  user_id: signUpData.user.id,
                  credits_limit: 100,
                  credits_used: 0
                });
                
              if (simpleInsertError) {
                console.error("Simple insert error for vita credits:", simpleInsertError);
              } else {
                console.log("Vita credits created with simple insert");
              }
            } else {
              console.log("Vita credits created:", creditData);
            }
          } else {
            console.log("User already has credits:", existingCredits);
          }
          
        } catch (profileError: any) {
          console.error("Error saving profile data:", profileError);
          // If profile saving fails, continue with email verification
          toast({
            title: "Profile Data Warning",
            description: "Account created but there was an issue saving your profile data: " + profileError.message,
            variant: "destructive"
          });
        }
      }

      // Mark onboarding as complete
      setIsOnboardingComplete(true);
      
      // Show success message
      toast({
        title: "Registration Complete",
        description: "Your account has been created. Please check your email to verify your account.",
      });
      
      // Navigate to verification page
      navigate('/auth?mode=verification_sent');
      
    } catch (error: any) {
      console.error('Error during registration:', error);
      toast({
        title: "Registration Error",
        description: error.message || 'Please try again.',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    onboardingStep,
    handleInputChange,
    handleCheckboxChange,
    handleSelectChange,
    handleRadioChange,
    nextStep,
    prevStep,
    handleSubmit
  };
}
